import type { RaceParticipant } from "../core/race";

export interface AudioMix {
  engineGain: number;
  packFrequency: number;
  packGain: number;
  windGain: number;
  skidGain: number;
}

export interface ShepardVoice {
  frequency: number;
  gain: number;
}

export function shepardVoiceMix(phase: number): ShepardVoice[] {
  const wrapped = ((phase % 1) + 1) % 1;
  const base = 32 * 2 ** wrapped;
  const center = Math.log2(Math.sqrt(64 * 128));
  const halfSpan = 1.5;
  const voices = [base, base * 2, base * 4, base * 8].map((frequency) => {
    const distance = Math.abs(Math.log2(frequency) - center) / halfSpan;
    const gain = Math.max(0, Math.cos(Math.min(1, distance) * Math.PI / 2)) ** 2;
    return { frequency, gain };
  });
  const total = Math.max(1e-9, voices.reduce((sum, voice) => sum + voice.gain, 0));
  return voices.map((voice) => ({ ...voice, gain: voice.gain / total }));
}

export function preferredFocusId(participants: RaceParticipant[], leaderId: string | null): string | null {
  const human = participants.find((participant) => participant.driver === null);
  return human?.selection.slotId ?? leaderId ?? participants[0]?.selection.slotId ?? null;
}

export function audioMix(participants: RaceParticipant[], focusId: string | null, active: boolean): AudioMix {
  const focus = participants.find((participant) => participant.selection.slotId === focusId);
  if (!focus || !active || focus.status !== "RACING") {
    return { engineGain: 0, packFrequency: 48, packGain: 0, windGain: 0, skidGain: 0 };
  }
  const maximumSpeed = Math.max(1, focus.vehicle.physics.max_forward_speed);
  const speedRatio = Math.min(1, Math.abs(focus.car.speed) / maximumSpeed);
  const throttle = Math.max(0, focus.control.throttleBrake);
  const airborne = focus.car.airborne || focus.car.z > 0.35;
  const others = participants.filter((participant) => participant !== focus && participant.status === "RACING");
  const averagePackRatio = others.length === 0 ? 0 : others.reduce((sum, participant) => (
    sum + Math.min(1, Math.abs(participant.car.speed) / Math.max(1, participant.vehicle.physics.max_forward_speed))
  ), 0) / others.length;
  const steeringLoad = Math.abs(focus.control.steering) * speedRatio;
  return {
    engineGain: airborne ? 0 : 0.065 + 0.065 * speedRatio + 0.022 * throttle,
    packFrequency: 32 + 26 * averagePackRatio,
    packGain: others.length === 0 ? 0 : Math.min(0.012, 0.003 + others.length * 0.0016) * averagePackRatio,
    windGain: airborne ? 0.006 + 0.017 * speedRatio : 0,
    skidGain: airborne || steeringLoad < 0.52 ? 0 : Math.min(0.019, (steeringLoad - 0.52) * 0.034),
  };
}
